/**
 * Journal export to plain text / Markdown (the data belongs to the
 * user — a privacy pillar of PRODUCT_PLAN). Pure logic.
 */

export interface ExportableEntry {
  entry_date: string | null;
  title: string | null;
  body: string | null;
  mood: number | null;
  created_at: string | null;
  /** Resolved tag names. */
  tags?: string[];
  /** Photo filenames. */
  photos?: string[];
}

const MOOD_GLYPHS: Record<number, string> = {
  1: "😞",
  2: "😕",
  3: "😐",
  4: "🙂",
  5: "😄",
};

function timeOf(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const PAD = 2;
  return `${String(date.getHours()).padStart(PAD, "0")}:${String(date.getMinutes()).padStart(PAD, "0")}`;
}

function groupByDate(entries: readonly ExportableEntry[]): Map<string, ExportableEntry[]> {
  const groups = new Map<string, ExportableEntry[]>();
  for (const entry of entries) {
    const key = entry.entry_date ?? "";
    const group = groups.get(key);
    if (group) {
      group.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return groups;
}

/** Markdown: one H1 per day, H2 per entry (time/title), mood and tags. */
export function entriesToMarkdown(entries: readonly ExportableEntry[]): string {
  const parts: string[] = [];
  for (const [date, dayEntries] of groupByDate(entries)) {
    parts.push(`# ${date}`);
    for (const entry of dayEntries) {
      const time = timeOf(entry.created_at);
      const heading = entry.title ? `## ${entry.title}` : `## ${time || date}`;
      parts.push(heading);
      const meta: string[] = [];
      if (entry.title && time) {
        meta.push(`🕐 ${time}`);
      }
      if (entry.mood) {
        meta.push(`${MOOD_GLYPHS[entry.mood] ?? ""} ${entry.mood}/5`.trim());
      }
      if (entry.tags && entry.tags.length > 0) {
        meta.push(entry.tags.map((t) => `#${t.replaceAll(/\s+/gu, "-")}`).join(" "));
      }
      if (meta.length > 0) {
        parts.push(`> ${meta.join(" · ")}`);
      }
      parts.push(entry.body ?? "");
      if (entry.photos && entry.photos.length > 0) {
        parts.push(entry.photos.map((p) => `📷 ${p}`).join("\n"));
      }
    }
  }
  return `${parts.join("\n\n").trim()}\n`;
}

/** Plain text: readable anywhere, without syntax. */
export function entriesToPlainText(entries: readonly ExportableEntry[]): string {
  const parts: string[] = [];
  for (const [date, dayEntries] of groupByDate(entries)) {
    parts.push(`${date}\n${"=".repeat(date.length)}`);
    for (const entry of dayEntries) {
      const headerBits = [timeOf(entry.created_at), entry.title ?? ""].filter(Boolean);
      if (headerBits.length > 0) {
        parts.push(headerBits.join(" — "));
      }
      if (entry.mood) {
        parts.push(`Ánimo: ${entry.mood}/5`);
      }
      if (entry.tags && entry.tags.length > 0) {
        parts.push(`Etiquetas: ${entry.tags.join(", ")}`);
      }
      parts.push(entry.body ?? "");
    }
  }
  return `${parts.join("\n\n").trim()}\n`;
}

/** Suggested filename for a range export. */
export function journalExportFilename(startKey: string, endKey: string, ext: "md" | "txt"): string {
  return `journal_${startKey}_${endKey}.${ext}`;
}
